import { TextFieldInput } from 'types/mounting'
import { StudioSystemType } from 'types/studio/items'
import { getSystemRoofType } from '../../compatibility'
import { RoofTypeName } from '../../types'
import {
  getAttachmentOptionsForRoofAndStyle,
  getCompatibleStylesForRoofType,
  getDefaultAttachmentCode,
  ROOF_ATTACHMENT_INPUT_LABEL,
} from './UltraRailAttachments'
import type { AttachmentOption, UltraRailMountingType } from './UltraRailTypes'
import { validatePZSEAttachmentSpacing, PZSE_DEFAULT_ATTACHMENT_SPACING_INCHES } from './PZSE'

export function getSnapnrackRafterSpacingDefault(mountingType: UltraRailMountingType): number {
  return mountingType === 'Decking' ? 1 : 24
}

export function resolveSnapnrackAttachmentCode(
  attachmentOptions: AttachmentOption[],
  roofType: RoofTypeName | null,
  currentCode?: string
): string {
  if (currentCode && attachmentOptions.some((o) => o.code === currentCode)) {
    return currentCode
  }
  const defaultAttachment = getDefaultAttachmentCode(roofType) as string
  if (attachmentOptions.some((o) => o.code === defaultAttachment)) return defaultAttachment
  return attachmentOptions[0]?.code ?? ''
}

/** Shared Attachment Style + Roof Attachment fields (mounting tab and PZSE dialog). */
export function getSnapnrackMountingAttachmentFieldSpecs(
  system: StudioSystemType,
  overrides?: {
    mountingType?: UltraRailMountingType
    attachment?: string
  }
): { mountingType: SnapnrackPZSESelectField; attachment: SnapnrackPZSESelectField } {
  const integrationData = (system?.integration_json?.snapnrackUltraRail || {}) as Record<string, unknown>
  const roofType = getSystemRoofType() as RoofTypeName | null
  const mountingType = (overrides?.mountingType ??
    integrationData.mountingType ??
    'Rafter') as UltraRailMountingType
  const compatibleStyles = getCompatibleStylesForRoofType(roofType)
  const attachmentOptions = roofType ? getAttachmentOptionsForRoofAndStyle(roofType, mountingType) : []
  const currentAttachment = overrides?.attachment ?? (integrationData.attachment as string | undefined)
  const defaultAttachmentCode = resolveSnapnrackAttachmentCode(attachmentOptions, roofType, currentAttachment)

  return {
    mountingType: {
      kind: 'select',
      label: 'Attachment Style',
      variableName: 'mountingType',
      default: mountingType,
      options: compatibleStyles.map((style) => ({ label: style, value: style })),
    },
    attachment: {
      kind: 'select',
      label: ROOF_ATTACHMENT_INPUT_LABEL,
      variableName: 'attachment',
      default: defaultAttachmentCode,
      options: attachmentOptions.map((o) => ({ label: o.label, value: o.code })),
    },
  }
}

export type SnapnrackPZSESelectField = {
  kind: 'select'
  label: string
  variableName: string
  default: string | number
  options: { label: string; value: string | number }[]
}

export type SnapnrackPZSENumberField = {
  kind: 'number'
  label: string
  variableName: string
  default: number
  min?: number
  max?: number
}

export type SnapnrackPZSEFieldSpec = SnapnrackPZSESelectField | SnapnrackPZSENumberField

export type SnapnrackPZSEFieldValues = Record<string, string | number>

const PZSE_SELECT_FIELDS: SnapnrackPZSESelectField[] = [
  {
    kind: 'select',
    label: 'Roof Structure Type',
    variableName: 'roofStructureType',
    default: 'gable',
    options: [
      { label: 'Gable', value: 'gable' },
      { label: 'Hip', value: 'hip' },
    ],
  },
  {
    kind: 'select',
    label: 'Design Code',
    variableName: 'asce7Version',
    default: 'ASCE 7-16',
    options: [
      { label: 'ASCE 7-16', value: 'ASCE 7-16' },
      { label: 'ASCE 7-22', value: 'ASCE 7-22' },
    ],
  },
  {
    kind: 'select',
    label: 'Thermal Factor',
    variableName: 'thermalFactor',
    default: 1.1,
    options: [
      { label: '1.0', value: 1.0 },
      { label: '1.1', value: 1.1 },
      { label: '1.2', value: 1.2 },
    ],
  },
  {
    kind: 'select',
    label: 'Room to slide?',
    variableName: 'roomToSlide',
    default: 'Yes',
    options: [
      { label: 'Yes', value: 'Yes' },
      { label: 'No', value: 'No' },
    ],
  },
  {
    kind: 'select',
    label: 'Slippery surface?',
    variableName: 'slipperySurface',
    default: 'No',
    options: [
      { label: 'Yes', value: 'Yes' },
      { label: 'No', value: 'No' },
    ],
  },
  {
    kind: 'select',
    label: 'Reducible snow load?',
    variableName: 'reducibleSnowLoad',
    default: 'Yes',
    options: [
      { label: 'Yes', value: 'Yes' },
      { label: 'No', value: 'No' },
    ],
  },
  {
    kind: 'select',
    label: 'Snow exposure',
    variableName: 'snowExposure',
    default: 'Partially Exposed',
    options: [
      { label: 'Exposed', value: 'Exposed' },
      { label: 'Partially Exposed', value: 'Partially Exposed' },
      { label: 'Sheltered', value: 'Sheltered' },
    ],
  },
  {
    kind: 'select',
    label: 'Wind exposure',
    variableName: 'windExposure',
    default: 'B',
    options: [
      { label: 'B', value: 'B' },
      { label: 'C', value: 'C' },
      { label: 'D', value: 'D' },
    ],
  },
]

function getNumberFields(system: StudioSystemType): SnapnrackPZSENumberField[] {
  const integrationData = (system?.integration_json?.snapnrackUltraRail || {}) as Record<string, unknown>
  const mountingType = (integrationData.mountingType ?? 'Rafter') as UltraRailMountingType
  const rafterSpacingDefault = getSnapnrackRafterSpacingDefault(mountingType)

  return [
    {
      kind: 'number',
      label: 'Rafter spacing (inches)',
      variableName: 'rafterSpacing',
      default: rafterSpacingDefault,
      min: 1,
    },
    {
      kind: 'number',
      label: 'Mean roof height (ft)',
      variableName: 'meanRoofHeight',
      default: 0,
    },
    {
      kind: 'number',
      label: 'Least horizontal building dimension (ft)',
      variableName: 'leastHorizontalBuildingDimension',
      default: 22,
    },
  ]
}

export function getSnapnrackPZSEAttachmentSpacingField(): SnapnrackPZSENumberField {
  return {
    kind: 'number',
    label: 'Attachment spacing (inches)',
    variableName: 'attachmentSpacingInches',
    default: PZSE_DEFAULT_ATTACHMENT_SPACING_INCHES,
    min: 24,
    max: 96,
  }
}

/** Mounting-tab text field — same spec as the PZSE dialog attachment spacing input. */
export function getSnapnrackAttachmentSpacingTextFieldInput(): TextFieldInput {
  const field = getSnapnrackPZSEAttachmentSpacingField()
  return {
    label: field.label,
    variableName: field.variableName,
    default: field.default,
    type: 'number',
    min: field.min,
    max: field.max,
  }
}

/** Left / right column field order for the PZSE dialog (not grouped by input type). */
export function getSnapnrackPZSEDialogColumns(system: StudioSystemType): {
  left: SnapnrackPZSEFieldSpec[]
  right: SnapnrackPZSEFieldSpec[]
  attachmentSpacing: SnapnrackPZSENumberField
  mountingAttachment: { mountingType: SnapnrackPZSESelectField; attachment: SnapnrackPZSESelectField }
} {
  const numberFields = getNumberFields(system)
  const rafterSpacing = numberFields.find((f) => f.variableName === 'rafterSpacing')!
  const meanRoofHeight = numberFields.find((f) => f.variableName === 'meanRoofHeight')!
  const leastHorizontal = numberFields.find((f) => f.variableName === 'leastHorizontalBuildingDimension')!

  const byName = (name: string) => PZSE_SELECT_FIELDS.find((f) => f.variableName === name)!

  return {
    left: [
      byName('roofStructureType'),
      byName('asce7Version'),
      byName('thermalFactor'),
      byName('roomToSlide'),
      byName('slipperySurface'),
      byName('reducibleSnowLoad'),
    ],
    right: [
      byName('snowExposure'),
      byName('windExposure'),
      meanRoofHeight,
      leastHorizontal,
      rafterSpacing,
    ],
    attachmentSpacing: getSnapnrackPZSEAttachmentSpacingField(),
    mountingAttachment: getSnapnrackMountingAttachmentFieldSpecs(system),
  }
}

export function getSnapnrackPZSEFieldDefaults(system: StudioSystemType): SnapnrackPZSEFieldValues {
  const { left, right, attachmentSpacing, mountingAttachment } = getSnapnrackPZSEDialogColumns(system)
  const values: SnapnrackPZSEFieldValues = {}

  ;[...left, ...right, attachmentSpacing, mountingAttachment.mountingType, mountingAttachment.attachment].forEach(
    (field) => {
      values[field.variableName] = field.default
    }
  )

  return values
}

export function readSnapnrackPZSEFieldValues(
  system: StudioSystemType,
  mountingJson: Record<string, unknown> | undefined
): SnapnrackPZSEFieldValues {
  const defaults = getSnapnrackPZSEFieldDefaults(system)
  const values = { ...defaults }

  if (mountingJson) {
    Object.keys(defaults).forEach((key) => {
      if (mountingJson[key] !== undefined && mountingJson[key] !== null) {
        values[key] = mountingJson[key] as string | number
      }
    })
  }

  return values
}

export function applySnapnrackPZSEFields(
  system: StudioSystemType,
  fields: SnapnrackPZSEFieldValues,
  options?: { dispatchMounting?: boolean }
): void {
  if (!system.integration_json) {
    system.integration_json = {}
  }
  if (!system.integration_json.snapnrackUltraRail) {
    system.integration_json.snapnrackUltraRail = {}
  }

  system.integration_json.snapnrackUltraRail = {
    ...system.integration_json.snapnrackUltraRail,
    ...fields,
  }

  const attachmentSpacing = fields.attachmentSpacingInches
  if (attachmentSpacing != null) {
    validatePZSEAttachmentSpacing(system.uuid, Number(attachmentSpacing))
  }

  if (options?.dispatchMounting !== false && window.editor?.signals) {
    window.editor.signals.objectChanged.dispatch(system, 'mounting')
  }
}
